import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Building2, LogOut, MapPin, Bed, Bath, Square, ArrowRight, 
  User, Mail, Calendar, TrendingUp, Lock, CheckCircle2,
  Clock, AlertTriangle, CalendarDays, Home, FileCheck,
  DollarSign, Search, Sparkles, X, ChevronDown, ChevronUp,
  Plus, Minus, Briefcase, Trash2, Edit3, Save, FileText, Download,
  History, Send, Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";
import BuildingComparison from "@/pages/BuildingComparison";
import ReplacementFinder from "@/pages/ReplacementFinder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- 1031-eligible closing-cost estimates (all-cash purchase) -----
// These rates reflect ONLY costs that can be paid from 1031 exchange funds
// without creating taxable "boot" per IRS Rev. Rul. 72-456 and
// Treas. Reg. §1.1031(k)-1(g)(7). Mortgage-related, operating, and
// prorated costs must be paid from outside funds.
//
// NYC — Sponsor sale of a new-development condo ≥ $1M:
//   Mansion Tax                              1.00–2.00% (tier-based)
//   NYS + NYC transfer-tax reimbursement     ~1.825% (buyer pays sponsor's)
//   Owner's title insurance                  ~0.45%
//   Attorney fee                             ~0.10%
//   Recording fees                           negligible
//   → Typical eligible total: ~3.0%
//   (Resale is lower, ~1.5–2%, because buyer doesn't reimburse transfer tax.)
//
// Miami — Sponsor sale of a new-development condo:
//   Doc stamps on deed (Miami-Dade)          0.60% (buyer on sponsor sales)
//   Owner's title insurance                  ~0.55% (promulgated rate)
//   Attorney fee                             ~0.10%
//   Recording + minor fees                   negligible
//   → Typical eligible total: ~1.5%
//
// EXCLUDED from 1031-eligible (must be paid separately, create BOOT):
//   • Mortgage origination / lender's title / points / appraisal-for-lender
//   • HOA transfer / move-in / estoppel fees (operating, not transactional)
//   • Prorated HOA dues, property tax, insurance, utilities
//   • Security deposits, prepaid rents
//
// Clients must confirm specific eligibility with their Qualified Intermediary.
const CLOSING_COST_RATES = {
  nyc: 0.030,
  miami: 0.015,
};
const DEFAULT_CLOSING_RATE = 0.025;

// Compute weighted closing-cost rate for a mixed-market portfolio
const portfolioClosingRate = (properties) => {
  if (!properties || properties.length === 0) return DEFAULT_CLOSING_RATE;
  const totalValue = properties.reduce((s, p) => s + (p.price || 0), 0);
  if (totalValue <= 0) return DEFAULT_CLOSING_RATE;
  const weighted = properties.reduce((s, p) => {
    const rate = CLOSING_COST_RATES[(p.market || "").toLowerCase()] ?? DEFAULT_CLOSING_RATE;
    return s + (p.price || 0) * rate;
  }, 0);
  return weighted / totalValue;
};

/**
 * Given a portfolio's asking price and the buyer's 1031 funds, compute:
 *   - estimated closing costs (based on market mix & asking price)
 *   - funds left for the actual property purchase after closing
 *   - the percent the sponsor needs to cut from asking so total-cost-to-buyer = 1031 funds
 *
 * Math: negotiated * (1 + closingRate) = 1031funds
 *       → negotiated = 1031funds / (1 + closingRate)
 *       → sponsor_cut_pct = 1 - (negotiated / asking)
 */
const analyzePortfolioCosts = (properties, totalValue, funds) => {
  const rate = portfolioClosingRate(properties);
  const fundsForProperty = funds > 0 ? funds / (1 + rate) : 0;
  const sponsorCutPct =
    totalValue > 0 && fundsForProperty > 0
      ? Math.max(0, (1 - fundsForProperty / totalValue) * 100)
      : 0;
  const estimatedClosingAtAsking = totalValue * rate;
  return {
    closingRate: rate,
    closingRatePct: rate * 100,
    estimatedClosingAtAsking,
    fundsForProperty,
    sponsorCutPct,
  };
};

// ----- Style helpers (extracted to avoid nested-ternary noise in JSX) -----

// Cap-rate color buckets
const capRateTextColor = (rate) => {
  if (rate >= 5) return 'text-green-600';
  if (rate >= 3) return 'text-yellow-600';
  return 'text-gray-600';
};

// Generic deadline label color (for "Day 45" / "Day 180" markers)
const deadlineLabelColor = (passed, critical, normalColor = 'text-gray-600') => {
  if (passed) return 'text-red-600';
  if (critical) return 'text-yellow-600';
  return normalColor;
};

// Marker dot color on the timeline progress track
const milestoneDotColor = (passed, critical) => {
  if (passed) return 'bg-red-500';
  if (critical) return 'bg-yellow-500 animate-pulse';
  return 'bg-gray-300';
};

// Progress bar fill color on the 1031 timeline
const timelineProgressColor = (data) => {
  if (data.is180Passed) return 'bg-red-500';
  if (data.is45Passed) return 'bg-yellow-500';
  return 'bg-[#0066FF]';
};

// Composite style for an entire deadline card
// `neutralPalette` is the default (non-passed, non-critical) classes
const deadlineCardStyle = (passed, critical, neutralPalette) => {
  if (passed) return {
    container: 'bg-red-50 border border-red-200',
    icon: 'text-red-600', heading: 'text-red-800',
    value: 'text-red-700', body: 'text-red-600',
  };
  if (critical) return {
    container: 'bg-yellow-50 border-2 border-yellow-400',
    icon: 'text-yellow-600', heading: 'text-yellow-800',
    value: 'text-yellow-700', body: 'text-yellow-600',
  };
  return neutralPalette;
};

// Deal-room target/budget styling
const dealRoomTargetStyle = (remainingFunds) => {
  if (Math.abs(remainingFunds) < 1000) return {
    container: 'bg-green-50 border border-green-300',
    label: 'Matched!', value: 'text-green-600',
  };
  if (remainingFunds > 0) return {
    container: 'bg-blue-50 border border-blue-200',
    label: 'Remaining', value: 'text-blue-600',
  };
  return {
    container: 'bg-red-50 border border-red-200',
    label: 'Over Budget', value: 'text-red-600',
  };
};

const DEADLINE_45_NEUTRAL = {
  container: 'bg-blue-50 border border-blue-200',
  icon: 'text-blue-600', heading: 'text-blue-800',
  value: 'text-blue-700', body: 'text-blue-600',
};
const DEADLINE_180_NEUTRAL = {
  container: 'bg-gray-50 border border-gray-200',
  icon: 'text-gray-600', heading: 'text-gray-800',
  value: 'text-gray-700', body: 'text-gray-600',
};

export default function ClientPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [activePortalTab, setActivePortalTab] = useState("inventory");
  
  // Timeline state
  const [saleDate, setSaleDate] = useState(() => {
    const saved = localStorage.getItem('1031_sale_date');
    return saved || '';
  });
  const [showTimeline, setShowTimeline] = useState(true);

  // Relinquished property state
  const [relinquishedValue, setRelinquishedValue] = useState(() => {
    const saved = localStorage.getItem('1031_relinquished_value');
    return saved || '';
  });
  const [relinquishedType, setRelinquishedType] = useState(() => {
    const saved = localStorage.getItem('1031_relinquished_type');
    return saved || '';
  });
  const [relinquishedLocation, setRelinquishedLocation] = useState(() => {
    const saved = localStorage.getItem('1031_relinquished_location');
    return saved || '';
  });

  // Property finder state
  const [buildingPreference, setBuildingPreference] = useState("any"); // "any", "same", "multiple"
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Deal Room state
  const [dealRoomProperties, setDealRoomProperties] = useState(() => {
    const saved = localStorage.getItem('1031_deal_room');
    return saved ? JSON.parse(saved) : [];
  });
  const [showDealRoom, setShowDealRoom] = useState(true);

  // LOI (Letter of Intent) state
  const [lois, setLois] = useState([]);
  const [loadingLois, setLoadingLois] = useState(false);
  const [showLoiModal, setShowLoiModal] = useState(false);
  const [editingLoi, setEditingLoi] = useState(null);
  const [loiProperty, setLoiProperty] = useState(null);
  const [loiForm, setLoiForm] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    buyer_address: '',
    buyer_entity_name: '',
    earnest_money_percent: 10,
    inspection_period_days: 14,
    financing_contingency_days: 30,
    closing_days_from_acceptance: 60,
    closing_date: '',
    additional_terms: '',
    exclusions: ''
  });

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!location.state?.user) {
      checkAuth();
    }
  }, [location.state, checkAuth]);

  const fetchProperties = useCallback(async () => {
    setLoadingProperties(true);
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data);
    } catch (error) {
      logger.error("Failed to fetch properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoadingProperties(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProperties();
    }
  }, [user, fetchProperties]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      logger.error("Logout error:", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Timeline calculations
  const handleSaleDateChange = (date) => {
    setSaleDate(date);
    localStorage.setItem('1031_sale_date', date);
  };

  const getTimelineData = () => {
    if (!saleDate) return null;
    
    const sale = new Date(saleDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const day45 = new Date(sale);
    day45.setDate(day45.getDate() + 45);
    
    const day180 = new Date(sale);
    day180.setDate(day180.getDate() + 180);
    
    const daysSinceSale = Math.floor((today - sale) / (1000 * 60 * 60 * 24));
    const daysUntil45 = Math.floor((day45 - today) / (1000 * 60 * 60 * 24));
    const daysUntil180 = Math.floor((day180 - today) / (1000 * 60 * 60 * 24));
    
    const progress45 = Math.min(100, Math.max(0, (daysSinceSale / 45) * 100));
    const progress180 = Math.min(100, Math.max(0, (daysSinceSale / 180) * 100));
    
    return {
      saleDate: sale,
      day45Date: day45,
      day180Date: day180,
      daysSinceSale,
      daysUntil45,
      daysUntil180,
      progress45,
      progress180,
      is45Passed: daysUntil45 < 0,
      is180Passed: daysUntil180 < 0,
      is45Critical: daysUntil45 >= 0 && daysUntil45 <= 7,
      is180Critical: daysUntil180 >= 0 && daysUntil180 <= 14,
    };
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const timelineData = getTimelineData();

  // Relinquished property handlers
  const handleRelinquishedValueChange = (value) => {
    setRelinquishedValue(value);
    localStorage.setItem('1031_relinquished_value', value);
  };

  const handleRelinquishedTypeChange = (value) => {
    setRelinquishedType(value);
    localStorage.setItem('1031_relinquished_type', value);
  };

  const handleRelinquishedLocationChange = (value) => {
    setRelinquishedLocation(value);
    localStorage.setItem('1031_relinquished_location', value);
  };

  // Property suggestion algorithm
  const suggestedPortfolios = useMemo(() => {
    const targetValue = parseFloat(relinquishedValue) || 0;
    if (targetValue <= 0 || properties.length === 0) return [];

    const availableProperties = properties.filter(p => p.status === "available");
    
    // Group properties by building
    const byBuilding = {};
    availableProperties.forEach(p => {
      const key = p.building_name || p.address || 'Unknown';
      if (!byBuilding[key]) byBuilding[key] = [];
      byBuilding[key].push(p);
    });

    const findBestCombination = (props, funds, maxProps = 5) => {
      // Sort by cap rate (descending) then price (ascending) for best value
      const sorted = [...props].sort((a, b) => {
        const capDiff = (b.cap_rate || 0) - (a.cap_rate || 0);
        if (Math.abs(capDiff) > 0.5) return capDiff;
        return a.price - b.price;
      });

      // Target a combined-asking total that yields a 5-7% sponsor cut AFTER
      // accounting for closing costs. We use the average of NYC+Miami rates
      // as a planning fallback; per-portfolio closing is computed later.
      //   negotiated = funds / (1 + closingRate)
      //   totalValue for 5% cut = negotiated / 0.95
      //   totalValue for 7% cut = negotiated / 0.93
      const avgClosingRate =
        (CLOSING_COST_RATES.nyc + CLOSING_COST_RATES.miami) / 2;
      const negotiatedTarget = funds / (1 + avgClosingRate);
      const targetMin = negotiatedTarget / 0.95; // 5% cut
      const targetMax = negotiatedTarget / 0.93; // 7% cut
      const idealMid = negotiatedTarget / 0.94;  // 6% cut midpoint
      const fillUpTo = negotiatedTarget;         // phase 1 ceiling

      // Phase 1: Greedily fill UP TO the fillUpTo line (net of closing)
      const result = [];
      let total = 0;
      for (const prop of sorted) {
        if (result.length >= maxProps) break;
        if (total + prop.price <= fillUpTo) {
          result.push(prop);
          total += prop.price;
        }
      }

      // Phase 2: Add ONE more unit so the combined asking lands in the
      // sweet-spot band (5-7% cut after closing).
      if (result.length < maxProps && total < fillUpTo) {
        const taken = new Set(result.map((p) => p.property_id));
        const remaining = sorted.filter((p) => !taken.has(p.property_id));

        const overCandidates = remaining
          .map((p) => ({ prop: p, newTotal: total + p.price }))
          .filter((c) => c.newTotal > fillUpTo);

        if (overCandidates.length > 0) {
          overCandidates.sort((a, b) => {
            const aInBand = a.newTotal >= targetMin && a.newTotal <= targetMax ? 0 : 1;
            const bInBand = b.newTotal >= targetMin && b.newTotal <= targetMax ? 0 : 1;
            if (aInBand !== bInBand) return aInBand - bInBand;
            return Math.abs(a.newTotal - idealMid) - Math.abs(b.newTotal - idealMid);
          });
          result.push(overCandidates[0].prop);
        }
      }

      return result;
    };

    const portfolios = [];

    // Filter by city preference first
    let cityFilteredProperties = availableProperties;
    if (buildingPreference === "nyc_only") {
      cityFilteredProperties = availableProperties.filter(p => p.market === "nyc");
    } else if (buildingPreference === "miami_only") {
      cityFilteredProperties = availableProperties.filter(p => p.market === "miami");
    }
    // "both_cities" and "any" use all properties

    // Re-group by building based on filtered properties
    const filteredByBuilding = {};
    cityFilteredProperties.forEach(p => {
      const building = p.building_name || 'Other';
      if (!filteredByBuilding[building]) filteredByBuilding[building] = [];
      filteredByBuilding[building].push(p);
    });

    // Option 1: Best overall combination (any buildings)
    if (buildingPreference === "any" || buildingPreference === "multiple" || buildingPreference === "nyc_only" || buildingPreference === "miami_only" || buildingPreference === "both_cities") {
      const bestOverall = findBestCombination(cityFilteredProperties, targetValue);
      if (bestOverall.length > 0) {
        const total = bestOverall.reduce((sum, p) => sum + p.price, 0);
        const avgCap = bestOverall.reduce((sum, p) => sum + (p.cap_rate || 0), 0) / bestOverall.length;
        const buildings = [...new Set(bestOverall.map(p => p.building_name || 'N/A'))];
        portfolios.push({
          id: 'best_overall',
          name: 'Combination Example',
          description: buildings.length > 1 ? `Across ${buildings.length} buildings` : `All in ${buildings[0]}`,
          properties: bestOverall,
          totalValue: total,
          remainingFunds: targetValue - total,
          avgCapRate: avgCap,
          buildingCount: buildings.length,
        });
      }
    }

    // Option 2: Same building options
    if (buildingPreference === "any" || buildingPreference === "same") {
      Object.entries(filteredByBuilding).forEach(([building, props]) => {
        if (props.length === 0) return;
        const combo = findBestCombination(props, targetValue);
        if (combo.length > 0) {
          const total = combo.reduce((sum, p) => sum + p.price, 0);
          const avgCap = combo.reduce((sum, p) => sum + (p.cap_rate || 0), 0) / combo.length;
          portfolios.push({
            id: `building_${building}`,
            name: `${building}`,
            description: 'Same building portfolio',
            properties: combo,
            totalValue: total,
            remainingFunds: targetValue - total,
            avgCapRate: avgCap,
            buildingCount: 1,
            isSameBuilding: true,
          });
        }
      });
    }

    // Sort: peak score lands in the 5-7% above-target sweet spot (negotiation
    // band). Outside that band scores drop off; significantly under or far
    // over are heavily penalized.
    return portfolios
      .filter(p => p.totalValue > 0)
      .sort((a, b) => {
        // Score based on sponsor-cut needed (after closing costs) — 5-7% is ideal.
        const scoreFor = (portfolio) => {
          const { sponsorCutPct } = analyzePortfolioCosts(
            portfolio.properties,
            portfolio.totalValue,
            targetValue,
          );
          if (sponsorCutPct >= 5 && sponsorCutPct <= 7) return 1.0;      // sweet spot
          if (sponsorCutPct > 7 && sponsorCutPct <= 10) return 0.85;     // above range
          if (sponsorCutPct >= 0 && sponsorCutPct < 5) return 0.75;      // below range
          if (sponsorCutPct < 0) return 0.60;                            // portfolio cheaper than needed
          return Math.max(0, 0.6 - (sponsorCutPct - 10) * 0.05);         // far above: rapid drop
        };
        const aScore = scoreFor(a);
        const bScore = scoreFor(b);
        if (Math.abs(aScore - bScore) > 0.02) return bScore - aScore;
        return b.avgCapRate - a.avgCapRate;
      })
      .slice(0, 5);
  }, [properties, relinquishedValue, buildingPreference]);

  // Deal Room functions
  const addToDealRoom = (property) => {
    const exists = dealRoomProperties.find(p => p.property_id === property.property_id);
    if (exists) {
      toast.info("Property already in Deal Room");
      return;
    }
    
    const dealRoomProperty = {
      ...property,
      offer_price: property.price, // Default offer price to asking price
      added_at: new Date().toISOString()
    };
    
    const updated = [...dealRoomProperties, dealRoomProperty];
    setDealRoomProperties(updated);
    localStorage.setItem('1031_deal_room', JSON.stringify(updated));
    toast.success("Added to Deal Room");
  };

  const removeFromDealRoom = (propertyId) => {
    const updated = dealRoomProperties.filter(p => p.property_id !== propertyId);
    setDealRoomProperties(updated);
    localStorage.setItem('1031_deal_room', JSON.stringify(updated));
    toast.success("Removed from Deal Room");
  };

  const updateOfferPrice = (propertyId, newPrice) => {
    const updated = dealRoomProperties.map(p => 
      p.property_id === propertyId 
        ? { ...p, offer_price: parseFloat(newPrice) || 0 }
        : p
    );
    setDealRoomProperties(updated);
    localStorage.setItem('1031_deal_room', JSON.stringify(updated));
  };

  const isInDealRoom = (propertyId) => {
    return dealRoomProperties.some(p => p.property_id === propertyId);
  };

  const clearDealRoom = () => {
    setDealRoomProperties([]);
    localStorage.removeItem('1031_deal_room');
    toast.success("Deal Room cleared");
  };

  // Deal Room calculations
  const dealRoomTotals = useMemo(() => {
    const totalAskingPrice = dealRoomProperties.reduce((sum, p) => sum + (p.price || 0), 0);
    const totalOfferPrice = dealRoomProperties.reduce((sum, p) => sum + (p.offer_price || 0), 0);
    const totalProjectedRent = dealRoomProperties.reduce((sum, p) => sum + (p.projected_monthly_rent || 0), 0);
    const totalCommonCharges = dealRoomProperties.reduce((sum, p) => sum + (p.monthly_common_charges || 0), 0);
    const totalPropertyTaxes = dealRoomProperties.reduce((sum, p) => sum + (p.monthly_taxes || 0), 0);
    const totalMonthlyExpenses = totalCommonCharges + totalPropertyTaxes;
    const totalMonthlyNetProfit = totalProjectedRent - totalMonthlyExpenses;
    const totalAnnualNetProfit = totalMonthlyNetProfit * 12;
    const avgCapRate = totalOfferPrice > 0 ? (totalAnnualNetProfit / totalOfferPrice) * 100 : 0;
    const relinquishedAmount = parseFloat(relinquishedValue) || 0;
    const remainingFunds = relinquishedAmount - totalOfferPrice;

    return {
      totalAskingPrice,
      totalOfferPrice,
      totalProjectedRent,
      totalCommonCharges,
      totalPropertyTaxes,
      totalMonthlyExpenses,
      totalMonthlyNetProfit,
      totalAnnualNetProfit,
      avgCapRate,
      relinquishedAmount,
      remainingFunds,
      propertyCount: dealRoomProperties.length
    };
  }, [dealRoomProperties, relinquishedValue]);

  // Memoized: LOIs that belong to properties currently in the deal room.
  // Avoids re-running this O(n*m) filter on every render of the deal room view.
  const dealRoomLois = useMemo(() => {
    if (!lois.length || !dealRoomProperties.length) return [];
    const ids = new Set(dealRoomProperties.map((p) => p.property_id));
    return lois.filter((l) => ids.has(l.property?.property_id));
  }, [lois, dealRoomProperties]);

  // ===== Budget Builder helpers (Suggested Portfolios redesign) =====

  // Selection vs. target derived state for the sticky progress bar
  const selectionTotals = useMemo(() => {
    const target = parseFloat(relinquishedValue) || 0;
    const total = dealRoomProperties.reduce((s, p) => s + (p.price || 0), 0);
    const remaining = target - total;
    const progressPct = target > 0 ? Math.min((total / target) * 100, 100) : 0;
    let status = 'empty';
    if (target > 0 && total > 0) {
      if (Math.abs(remaining) < 1000) status = 'matched';
      else if (remaining > 0) status = 'under';
      else status = 'over';
    }
    return { target, total, remaining, progressPct, status };
  }, [relinquishedValue, dealRoomProperties]);

  // Best-fit portfolio = closest to a 6% sponsor cut (after closing costs).
  const bestFitPortfolioId = useMemo(() => {
    if (!suggestedPortfolios.length) return null;
    const target = parseFloat(relinquishedValue) || 0;
    if (target <= 0) return suggestedPortfolios[0].id;
    const withCuts = suggestedPortfolios.map((p) => ({
      id: p.id,
      cutPct: analyzePortfolioCosts(p.properties, p.totalValue, target).sponsorCutPct,
    }));
    const inBand = withCuts.filter((x) => x.cutPct >= 5 && x.cutPct <= 7);
    const pool = inBand.length > 0 ? inBand : withCuts;
    return [...pool].sort(
      (a, b) => Math.abs(a.cutPct - 6) - Math.abs(b.cutPct - 6),
    )[0].id;
  }, [suggestedPortfolios, relinquishedValue]);

  // Replace current selection with this portfolio's units
  const loadPortfolio = (portfolio) => {
    const newSelection = portfolio.properties.map((prop) => ({
      ...prop,
      offer_price: prop.price || 0,
      added_at: new Date().toISOString(),
    }));
    setDealRoomProperties(newSelection);
    localStorage.setItem('1031_deal_room', JSON.stringify(newSelection));
    toast.success(`Loaded "${portfolio.name}" — ${portfolio.properties.length} units selected`);
  };

  // Add portfolio's units to current selection (skipping ones already in)
  const addPortfolioToCurrent = (portfolio) => {
    const existingIds = new Set(dealRoomProperties.map((p) => p.property_id));
    const toAdd = portfolio.properties.filter((p) => !existingIds.has(p.property_id));
    if (toAdd.length === 0) {
      toast.info("All units from this portfolio are already in your selection");
      return;
    }
    const additions = toAdd.map((prop) => ({
      ...prop,
      offer_price: prop.price || 0,
      added_at: new Date().toISOString(),
    }));
    const updated = [...dealRoomProperties, ...additions];
    setDealRoomProperties(updated);
    localStorage.setItem('1031_deal_room', JSON.stringify(updated));
    toast.success(`Added ${toAdd.length} unit${toAdd.length === 1 ? '' : 's'} to your selection`);
  };

  // Auto-fit: pick the best-matching suggested portfolio and load it
  const autoFitBudget = () => {
    if (!bestFitPortfolioId) {
      toast.error("No suggested portfolios available yet");
      return;
    }
    const best = suggestedPortfolios.find((p) => p.id === bestFitPortfolioId);
    if (best) loadPortfolio(best);
  };

  // Remove the most expensive unit (when over budget)
  const removeLargestUnit = () => {
    if (dealRoomProperties.length === 0) return;
    const largest = [...dealRoomProperties].sort((a, b) => (b.price || 0) - (a.price || 0))[0];
    removeFromDealRoom(largest.property_id);
  };

  const largestSelectedUnit = useMemo(() => {
    if (dealRoomProperties.length === 0) return null;
    return [...dealRoomProperties].sort((a, b) => (b.price || 0) - (a.price || 0))[0];
  }, [dealRoomProperties]);

  // Fit-to-budget badge per portfolio.
  // Target range: 5-7% sponsor cut AFTER closing costs. Labels explain what
  // the sponsor actually needs to do and whether it's realistic.
  const portfolioFitBadge = (portfolio, funds) => {
    if (funds <= 0 || portfolio.totalValue <= 0) {
      return { color: "bg-gray-100 text-gray-600", label: "—" };
    }
    const { sponsorCutPct } = analyzePortfolioCosts(
      portfolio.properties,
      portfolio.totalValue,
      funds,
    );
    if (sponsorCutPct < 1) {
      return {
        color: "bg-emerald-100 text-emerald-700",
        label: "Already within your funds — no negotiation needed",
      };
    }
    if (sponsorCutPct >= 5 && sponsorCutPct <= 7) {
      return {
        color: "bg-emerald-100 text-emerald-700",
        label: `Sponsor would need to cut ${sponsorCutPct.toFixed(1)}% — typical negotiation range`,
      };
    }
    if (sponsorCutPct < 5) {
      return {
        color: "bg-blue-100 text-blue-700",
        label: `Sponsor would need to cut only ${sponsorCutPct.toFixed(1)}% — below typical range`,
      };
    }
    if (sponsorCutPct <= 10) {
      return {
        color: "bg-amber-100 text-amber-800",
        label: `Sponsor would need to cut ${sponsorCutPct.toFixed(1)}% — above typical range, harder to negotiate`,
      };
    }
    return {
      color: "bg-red-100 text-red-700",
      label: `Sponsor would need to cut ${sponsorCutPct.toFixed(1)}% — unlikely to be accepted`,
    };
  };

  const progressBarColor = (() => {
    if (selectionTotals.status === 'matched') return 'bg-emerald-500';
    if (selectionTotals.status === 'over') return 'bg-amber-500';
    if (selectionTotals.status === 'under') return 'bg-[#0066FF]';
    return 'bg-gray-300';
  })();

  const progressMessage = (() => {
    if (selectionTotals.target === 0) return 'Enter your relinquished property value above to start';
    if (selectionTotals.status === 'empty') return 'Select properties below to allocate funds';
    if (selectionTotals.status === 'matched') return 'Target matched — ready to generate LOIs';
    if (selectionTotals.status === 'under') return `${formatPrice(selectionTotals.remaining)} left to allocate`;
    return `${formatPrice(Math.abs(selectionTotals.remaining))} over budget`;
  })();

  // LOI Functions
  const fetchLois = useCallback(async () => {
    if (!user) return;
    setLoadingLois(true);
    try {
      const response = await axios.get(`${API}/loi`, { withCredentials: true });
      setLois(response.data);
    } catch (error) {
      logger.error("Failed to fetch LOIs:", error);
    } finally {
      setLoadingLois(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchLois();
    }
  }, [user, fetchLois]);

  const openLoiModal = (property, existingLoi = null) => {
    setLoiProperty(property);
    if (existingLoi) {
      setEditingLoi(existingLoi);
      setLoiForm({
        buyer_name: existingLoi.buyer_name || user?.name || '',
        buyer_email: existingLoi.buyer_email || user?.email || '',
        buyer_phone: existingLoi.buyer_phone || '',
        buyer_address: existingLoi.buyer_address || '',
        buyer_entity_name: existingLoi.buyer_entity_name || '',
        earnest_money_percent: existingLoi.terms?.earnest_money_percent || 10,
        inspection_period_days: existingLoi.terms?.inspection_period_days || 14,
        financing_contingency_days: existingLoi.terms?.financing_contingency_days || 30,
        closing_days_from_acceptance: existingLoi.terms?.closing_days_from_acceptance || 60,
        closing_date: existingLoi.terms?.closing_date || '',
        additional_terms: existingLoi.terms?.additional_terms || '',
        exclusions: existingLoi.terms?.exclusions || ''
      });
    } else {
      setEditingLoi(null);
      setLoiForm({
        buyer_name: user?.name || '',
        buyer_email: user?.email || '',
        buyer_phone: '',
        buyer_address: '',
        buyer_entity_name: '',
        earnest_money_percent: 10,
        inspection_period_days: 14,
        financing_contingency_days: 30,
        closing_days_from_acceptance: 60,
        closing_date: '',
        additional_terms: '',
        exclusions: ''
      });
    }
    setShowLoiModal(true);
  };

  const closeLoiModal = () => {
    setShowLoiModal(false);
    setLoiProperty(null);
    setEditingLoi(null);
  };

  const handleLoiSubmit = async (e) => {
    e.preventDefault();
    if (!loiProperty) return;

    const loiData = {
      buyer_name: loiForm.buyer_name,
      buyer_email: loiForm.buyer_email || null,
      buyer_phone: loiForm.buyer_phone || null,
      buyer_address: loiForm.buyer_address || null,
      buyer_entity_name: loiForm.buyer_entity_name || null,
      property: {
        property_id: loiProperty.property_id,
        title: loiProperty.title,
        address: loiProperty.location || loiProperty.address || '',
        market: loiProperty.market,
        asking_price: loiProperty.price,
        offer_price: loiProperty.offer_price || loiProperty.price,
        bedrooms: loiProperty.bedrooms || 0,
        bathrooms: loiProperty.bathrooms || 0,
        sqft: loiProperty.sqft || 0
      },
      terms: {
        earnest_money_percent: parseFloat(loiForm.earnest_money_percent) || 10,
        earnest_money_deposit: (loiProperty.offer_price || loiProperty.price) * (parseFloat(loiForm.earnest_money_percent) / 100),
        inspection_period_days: parseInt(loiForm.inspection_period_days) || 14,
        financing_contingency_days: parseInt(loiForm.financing_contingency_days) || 30,
        closing_days_from_acceptance: parseInt(loiForm.closing_days_from_acceptance) || 60,
        closing_date: loiForm.closing_date || null,
        additional_terms: loiForm.additional_terms || null,
        exclusions: loiForm.exclusions || null
      }
    };

    try {
      if (editingLoi) {
        await axios.put(`${API}/loi/${editingLoi.loi_id}`, {
          buyer_name: loiData.buyer_name,
          buyer_email: loiData.buyer_email,
          buyer_phone: loiData.buyer_phone,
          buyer_address: loiData.buyer_address,
          buyer_entity_name: loiData.buyer_entity_name,
          terms: loiData.terms
        }, { withCredentials: true });
        toast.success("LOI updated successfully");
      } else {
        await axios.post(`${API}/loi`, loiData, { withCredentials: true });
        toast.success("LOI created successfully");
      }
      fetchLois();
      closeLoiModal();
    } catch (error) {
      logger.error("Failed to save LOI:", error);
      toast.error("Failed to save LOI");
    }
  };

  const downloadLoiPdf = async (loiId) => {
    try {
      const response = await axios.get(`${API}/loi/${loiId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'LOI.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("LOI PDF downloaded");
    } catch (error) {
      logger.error("Failed to download LOI:", error);
      toast.error("Failed to download LOI PDF");
    }
  };

  const deleteLoi = async (loiId) => {
    if (!window.confirm("Are you sure you want to delete this LOI?")) return;
    
    try {
      await axios.delete(`${API}/loi/${loiId}`, { withCredentials: true });
      toast.success("LOI deleted");
      fetchLois();
    } catch (error) {
      logger.error("Failed to delete LOI:", error);
      toast.error("Failed to delete LOI");
    }
  };

  const getLoiForProperty = (propertyId) => {
    return lois.find(loi => loi.property?.property_id === propertyId);
  };

  const formatLoiDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  // Not logged in - Show registration prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block px-3 py-1.5 bg-[#0066FF] text-white text-xs font-bold uppercase tracking-wider rounded mb-4">
                Client Portal
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-[#1A1A2E] leading-tight mb-6" data-testid="portal-title">
                Explore Potential 1031 Replacement Properties
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Register to view our full collection of luxury condo units available for 1031 exchanges in NYC and Miami. Get complete pricing, property details, and listing information.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "View complete property details and pricing",
                  "Browse potential replacement properties",
                  "Save properties to your watchlist",
                  "Receive new property alerts",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-[#0066FF]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mb-4">
                <Link to="/contact" className="block">
                  <Button
                    className="btn-primary w-full px-8 py-6 text-base rounded-lg font-semibold"
                    data-testid="portal-register-btn"
                  >
                    New Client? Register Here
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  onClick={handleLogin}
                  variant="outline"
                  className="w-full px-8 py-6 text-base rounded-lg font-semibold border-2"
                  data-testid="portal-login-btn"
                >
                  Already Registered? Sign in with Google
                </Button>
              </div>

              <p className="text-sm text-gray-500 mt-4">
                Registration is required before accessing our inventory. Please complete the brief intake form so a licensed broker can follow up with your replacement-property criteria.
              </p>

              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="client-login-disclaimer">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <span className="font-semibold">Disclaimer:</span> Krieger Real Estate LLC is a licensed real estate brokerage. This website is for informational purposes only. We do not provide legal, tax, or investment advice. All real estate transactions involve direct property ownership. Buyers should conduct their own independent due diligence.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Preview Cards - Blurred */}
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 blur-[2px]">
                      <div className="aspect-[4/3] bg-gray-200 rounded-lg mb-3" />
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  ))}
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center rounded-2xl">
                  <div className="text-center p-8">
                    <Lock className="w-12 h-12 text-[#0066FF] mx-auto mb-4" />
                    <p className="text-lg font-semibold text-[#1A1A2E]">Sign in to unlock</p>
                    <p className="text-sm text-gray-500">6 properties available</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Logged in - Show client dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-[#1A1A2E]">
              1031<span className="text-[#0066FF]">Condo</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-[#1A1A2E]">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-gray-600">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Welcome Banner */}
        <motion.div 
          className="bg-gradient-to-r from-[#0066FF] to-blue-600 rounded-2xl p-8 mb-8 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-2">Welcome back, {user.name?.split(' ')[0]}!</h1>
              <p className="text-blue-100">Explore potential 1031 replacement properties below.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/calculator">
                <Button className="bg-white/20 hover:bg-white/30 text-white border-white/30 border" data-testid="welcome-tax-calculator-btn">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Tax Calculator
                </Button>
              </Link>
              <Link
                to="/contact"
                state={{
                  consultationRequest: true,
                  prefillName: user.name,
                  prefillEmail: user.email,
                }}
              >
                <Button
                  className="bg-white text-[#0066FF] hover:bg-blue-50 font-semibold shadow-sm"
                  data-testid="welcome-request-consultation-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Request a Consultation
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Portal Tab Navigation */}
        <div className="flex gap-1 mb-8 bg-gray-100 p-1 rounded-xl w-fit" data-testid="portal-tabs">
          {[
            { key: "inventory", label: "Inventory" },
            { key: "compare", label: "Compare Buildings" },
            { key: "finder", label: "1031 Finder" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActivePortalTab(tab.key)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                activePortalTab === tab.key
                  ? "bg-[#0066FF] text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
              }`}
              data-testid={`portal-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Portal Disclaimer */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="portal-disclaimer">
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Important Notice:</span> Property information is provided for informational purposes only. Buyers should independently verify all details. All projected rents are estimates and may not reflect actual rental income achieved. Rental use and performance are subject to market conditions and local regulations. Common charges, taxes, and other expenses shown are approximate and subject to change. All sales pricing is based on current asking prices; final negotiated pricing for each unit may differ. Krieger Real Estate LLC is a licensed real estate brokerage and does not provide legal, tax, or investment advice. All real estate purchases involve risk.
          </p>
        </div>

        {activePortalTab === "compare" && (
          <div className="-mx-6 lg:-mx-12">
            <BuildingComparison embedded />
          </div>
        )}

        {activePortalTab === "finder" && (
          <div className="-mx-6 lg:-mx-12">
            <ReplacementFinder embedded />
          </div>
        )}

        {activePortalTab === "inventory" && (
        <>
        {/* 1031 Exchange Timeline Tool — collapsible */}
        <details className="bg-white rounded-2xl border border-gray-200 mb-8 shadow-sm group" data-testid="timeline-toggle">
          <summary className="flex items-center gap-3 p-4 cursor-pointer list-none">
            <CalendarDays className="w-5 h-5 text-[#0066FF]" />
            <span className="font-semibold text-[#1A1A2E]">1031 Exchange Timeline</span>
            <span className="text-sm text-gray-500 ml-2">
              {timelineData
                ? `${timelineData.daysUntil45 > 0 ? timelineData.daysUntil45 : 'Day 45 passed'} · ${timelineData.daysUntil180 > 0 ? timelineData.daysUntil180 : 'Day 180 passed'} days`
                : 'Track your 45-day and 180-day deadlines'}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400 ml-auto group-open:hidden" />
            <ChevronUp className="w-4 h-4 text-gray-400 ml-auto hidden group-open:block" />
          </summary>
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
        <motion.div 
          className="rounded-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-[#0066FF]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#1A1A2E]">1031 Exchange Timeline</h2>
                <p className="text-sm text-gray-500">Track your critical deadlines</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Sale Date:</label>
              <Input
                type="date"
                value={saleDate}
                onChange={(e) => handleSaleDateChange(e.target.value)}
                className="w-44"
                data-testid="sale-date-input"
              />
            </div>
          </div>

          {!saleDate ? (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
              <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Enter your property sale date above</p>
              <p className="text-sm text-gray-500">to see your 1031 exchange timeline</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Visual Timeline */}
              <div className="relative">
                {/* Progress Bar Background */}
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div 
                    className={`h-full rounded-full ${timelineProgressColor(timelineData)}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${timelineData.progress180}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </div>
                
                {/* Milestone Markers */}
                <div className="absolute top-0 left-0 w-full h-3 flex items-center">
                  {/* Day 0 Marker */}
                  <div className="absolute left-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow -translate-x-1/2" />
                  
                  {/* Day 45 Marker */}
                  <div 
                    className={`absolute w-4 h-4 rounded-full border-2 border-white shadow -translate-x-1/2 ${milestoneDotColor(timelineData.is45Passed, timelineData.is45Critical)}`}
                    style={{ left: '25%' }}
                  />
                  
                  {/* Day 180 Marker */}
                  <div 
                    className={`absolute w-4 h-4 rounded-full border-2 border-white shadow -translate-x-1/2 ${milestoneDotColor(timelineData.is180Passed, timelineData.is180Critical)}`}
                    style={{ left: '100%' }}
                  />
                </div>
              </div>

              {/* Timeline Labels */}
              <div className="flex justify-between text-sm">
                <div className="text-center">
                  <p className="font-semibold text-green-600">Day 0</p>
                  <p className="text-gray-500">Sale</p>
                </div>
                <div className="text-center" style={{ marginLeft: '15%' }}>
                  <p className={`font-semibold ${deadlineLabelColor(timelineData.is45Passed, timelineData.is45Critical)}`}>Day 45</p>
                  <p className="text-gray-500">Identification</p>
                </div>
                <div className="text-center">
                  <p className={`font-semibold ${deadlineLabelColor(timelineData.is180Passed, timelineData.is180Critical)}`}>Day 180</p>
                  <p className="text-gray-500">Closing</p>
                </div>
              </div>

              {/* Deadline Cards */}
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                {/* Sale Date Card */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Home className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Property Sold</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{formatDate(timelineData.saleDate)}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {timelineData.daysSinceSale} days ago
                  </p>
                </div>

                {/* 45-Day Deadline Card */}
                {(() => {
                  const s45 = deadlineCardStyle(timelineData.is45Passed, timelineData.is45Critical, DEADLINE_45_NEUTRAL);
                  return (
                    <div className={`rounded-xl p-4 ${s45.container}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck className={`w-5 h-5 ${s45.icon}`} />
                        <span className={`font-semibold ${s45.heading}`}>45-Day Identification</span>
                        {timelineData.is45Critical && !timelineData.is45Passed && (
                          <AlertTriangle className="w-4 h-4 text-yellow-500 animate-pulse" />
                        )}
                      </div>
                      <p className={`text-2xl font-bold ${s45.value}`}>
                        {formatDate(timelineData.day45Date)}
                      </p>
                      <p className={`text-sm mt-1 ${s45.body}`}>
                        {timelineData.is45Passed 
                          ? `Passed ${Math.abs(timelineData.daysUntil45)} days ago`
                          : `${timelineData.daysUntil45} days remaining`
                        }
                      </p>
                    </div>
                  );
                })()}

                {/* 180-Day Deadline Card */}
                {(() => {
                  const s180 = deadlineCardStyle(timelineData.is180Passed, timelineData.is180Critical, DEADLINE_180_NEUTRAL);
                  return (
                    <div className={`rounded-xl p-4 ${s180.container}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className={`w-5 h-5 ${s180.icon}`} />
                        <span className={`font-semibold ${s180.heading}`}>180-Day Closing</span>
                        {timelineData.is180Critical && !timelineData.is180Passed && (
                          <AlertTriangle className="w-4 h-4 text-yellow-500 animate-pulse" />
                        )}
                      </div>
                      <p className={`text-2xl font-bold ${s180.value}`}>
                        {formatDate(timelineData.day180Date)}
                      </p>
                      <p className={`text-sm mt-1 ${s180.body}`}>
                        {timelineData.is180Passed 
                          ? `Passed ${Math.abs(timelineData.daysUntil180)} days ago`
                          : `${timelineData.daysUntil180} days remaining`
                        }
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Alert Banner */}
              {(timelineData.is45Critical || timelineData.is180Critical) && !timelineData.is45Passed && !timelineData.is180Passed && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-yellow-800">Deadline Approaching!</p>
                    <p className="text-sm text-yellow-700">
                      {timelineData.is45Critical 
                        ? `Only ${timelineData.daysUntil45} days left to identify your replacement properties.`
                        : `Only ${timelineData.daysUntil180} days left to close on your replacement properties.`
                      }
                    </p>
                  </div>
                  <Link to="/contact" className="ml-auto">
                    <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700 text-white">
                      Get Help Now
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </motion.div>
          </div>
        </details>

        {/* Your 1031 Funds */}
        <motion.div 
          id="step1-funds"
          className="bg-white rounded-2xl border border-gray-200 p-6 mb-8 shadow-sm scroll-mt-24"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#1A1A2E]">Your 1031 Funds</h2>
            <p className="text-sm text-gray-500">Enter your available funds so we can suggest matching combinations.</p>
          </div>

          {/* Relinquished Property Details */}
          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <h3 className="font-semibold text-[#1A1A2E] mb-4">Your Relinquished Property</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Sale Value
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    type="number"
                    placeholder="5,000,000"
                    value={relinquishedValue}
                    onChange={(e) => handleRelinquishedValueChange(e.target.value)}
                    className="pl-7"
                    data-testid="relinquished-value"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Property Type
                </label>
                <Select value={relinquishedType} onValueChange={handleRelinquishedTypeChange}>
                  <SelectTrigger data-testid="relinquished-type">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential_rental">Residential Rental</SelectItem>
                    <SelectItem value="commercial">Commercial Property</SelectItem>
                    <SelectItem value="multifamily">Multi-Family (2-4 units)</SelectItem>
                    <SelectItem value="apartment_building">Apartment Building (5+ units)</SelectItem>
                    <SelectItem value="retail">Retail Property</SelectItem>
                    <SelectItem value="office">Office Building</SelectItem>
                    <SelectItem value="industrial">Industrial/Warehouse</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                    <SelectItem value="mixed_use">Mixed Use</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Location
                </label>
                <Input
                  placeholder="City, State"
                  value={relinquishedLocation}
                  onChange={(e) => handleRelinquishedLocationChange(e.target.value)}
                  data-testid="relinquished-location"
                />
              </div>
            </div>
          </div>

          {/* Property Type */}
          {relinquishedValue && parseFloat(relinquishedValue) > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Type
              </label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: "any", label: "Any Combination" },
                  { value: "same", label: "Same Building" },
                  { value: "multiple", label: "Multiple Buildings" },
                  { value: "nyc_only", label: "New York City Only" },
                  { value: "miami_only", label: "Miami Only" },
                  { value: "both_cities", label: "Both Cities" },
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setBuildingPreference(option.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      buildingPreference === option.value
                        ? 'bg-[#0066FF] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Portfolios */}
          {relinquishedValue && parseFloat(relinquishedValue) > 0 && (
            <div id="step2-portfolios" data-testid="budget-builder" className="scroll-mt-24">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A2E]">Suggested Combinations</h2>
                  <p className="text-sm text-gray-500">
                    Each suggestion is sized so that — after factoring in 1031-eligible NYC / Miami closing costs — a typical 5–7% sponsor price cut lets you close exactly at your available 1031 funds.{" "}
                    <button
                      type="button"
                      onClick={() => document.getElementById("closing-cost-details")?.scrollIntoView({ behavior: "smooth" })}
                      className="text-[#0066FF] underline hover:no-underline"
                    >
                      What's included?
                    </button>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {suggestedPortfolios.length > 0 && (
                    <span className="text-sm text-gray-500">{suggestedPortfolios.length} options</span>
                  )}
                  <Button
                    onClick={autoFitBudget}
                    variant="outline"
                    size="sm"
                    disabled={suggestedPortfolios.length === 0}
                    data-testid="auto-fit-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Auto-fit
                  </Button>
                </div>
              </div>

              {suggestedPortfolios.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No matching portfolios found</p>
                  <p className="text-sm text-gray-500">Try adjusting your criteria or check back for new inventory</p>
                </div>
              ) : (
                <div className="space-y-4 min-w-0">
                  {suggestedPortfolios.map((portfolio, idx) => {
                      const fit = portfolioFitBadge(portfolio, selectionTotals.target);
                      const isBest = portfolio.id === bestFitPortfolioId;
                      return (
                        <motion.div
                          key={portfolio.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(idx * 0.05, 0.3) }}
                          className={`bg-white rounded-xl overflow-hidden border-2 transition-shadow ${
                            isBest ? 'border-[#0066FF] shadow-md' : 'border-gray-200'
                          }`}
                          data-testid={`portfolio-card-${portfolio.id}`}
                        >
                          {/* Card Header */}
                          <div className="p-5 bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
                            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0">
                                {isBest && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0066FF] text-white text-xs font-semibold rounded">
                                    <Sparkles className="w-3 h-3" />
                                    Matches Value
                                  </span>
                                )}
                                <h4 className="font-semibold text-[#1A1A2E] truncate">{portfolio.name}</h4>
                              </div>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${fit.color}`}>
                                {fit.label}
                              </span>
                            </div>

                            <p className="text-sm text-gray-600 mb-4">{portfolio.description}</p>

                            {/* Combined Total vs 1031 Funds breakdown */}
                            {/* Closing-cost-aware breakdown */}
                            {(() => {
                              const target = parseFloat(relinquishedValue) || 0;
                              const analysis = analyzePortfolioCosts(
                                portfolio.properties,
                                portfolio.totalValue,
                                target,
                              );
                              const { closingRatePct, estimatedClosingAtAsking, fundsForProperty, sponsorCutPct } = analysis;
                              const isMatched = sponsorCutPct < 1;
                              const inSweetSpot = sponsorCutPct >= 5 && sponsorCutPct <= 7;
                              let cutColor = "text-[#0066FF]";
                              if (isMatched || inSweetSpot) cutColor = "text-emerald-600";
                              else if (sponsorCutPct > 7 && sponsorCutPct <= 10) cutColor = "text-amber-700";
                              else if (sponsorCutPct > 10) cutColor = "text-red-600";

                              const cutLabel = isMatched
                                ? "no negotiation needed"
                                : inSweetSpot
                                  ? `sponsor cuts ${sponsorCutPct.toFixed(1)}% (typical range)`
                                  : sponsorCutPct < 5
                                    ? `sponsor cuts only ${sponsorCutPct.toFixed(1)}% (below typical)`
                                    : sponsorCutPct <= 10
                                      ? `sponsor cuts ${sponsorCutPct.toFixed(1)}% (above typical)`
                                      : `sponsor cuts ${sponsorCutPct.toFixed(1)}% (unlikely)`;

                              return (
                                <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm space-y-1.5" data-testid={`portfolio-costs-${portfolio.id}`}>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Combined asking price</span>
                                    <span className="font-bold text-[#1A1A2E]">{formatPrice(portfolio.totalValue)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">
                                      1031-eligible closing costs <span className="text-gray-400">({closingRatePct.toFixed(1)}% {portfolio.properties.some((p) => p.market === "miami") && portfolio.properties.some((p) => p.market === "nyc") ? "blended NYC/Miami" : portfolio.properties[0]?.market === "miami" ? "Miami" : "NYC"})</span>
                                    </span>
                                    <span className="font-medium text-gray-700">+ {formatPrice(estimatedClosingAtAsking)}</span>
                                  </div>
                                  <div className="flex justify-between pt-1.5 border-t border-slate-200">
                                    <span className="text-gray-600">Your 1031 funds</span>
                                    <span className="font-medium text-[#1A1A2E]">{formatPrice(target)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Funds available for property (after closing)</span>
                                    <span className="font-medium text-[#1A1A2E]">{formatPrice(fundsForProperty)}</span>
                                  </div>
                                  <div className={`flex justify-between pt-1.5 border-t border-slate-200 font-semibold ${cutColor}`} data-testid={`portfolio-sponsor-cut-${portfolio.id}`}>
                                    <span>To close at your funds →</span>
                                    <span>{cutLabel}</span>
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="flex flex-wrap items-end justify-between gap-3">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="text-xs text-gray-500">
                                  {portfolio.properties.length} units
                                  · <span className={`font-medium ${capRateTextColor(portfolio.avgCapRate)}`}>
                                      {portfolio.avgCapRate.toFixed(2)}% avg cap*
                                    </span>
                                  · {portfolio.buildingCount} {portfolio.buildingCount === 1 ? 'building' : 'buildings'}
                                </span>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                <Button
                                  onClick={() => addPortfolioToCurrent(portfolio)}
                                  variant="outline"
                                  size="sm"
                                  data-testid={`add-portfolio-${portfolio.id}`}
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  Add to Current
                                </Button>
                                <Button
                                  onClick={() => loadPortfolio(portfolio)}
                                  size="sm"
                                  className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                                  data-testid={`use-portfolio-${portfolio.id}`}
                                >
                                  Use This Portfolio
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Always-visible property list with real checkboxes */}
                          <div className="divide-y divide-gray-100">
                            {portfolio.properties.map((prop) => {
                              const inSelection = isInDealRoom(prop.property_id);
                              return (
                                <label
                                  key={prop.property_id}
                                  htmlFor={`portfolio-${portfolio.id}-prop-${prop.property_id}`}
                                  className={`flex items-center gap-4 p-3 cursor-pointer transition-colors ${
                                    inSelection ? 'bg-blue-50/50' : 'hover:bg-gray-50'
                                  }`}
                                  data-testid={`portfolio-${portfolio.id}-prop-${prop.property_id}`}
                                >
                                  <Checkbox
                                    id={`portfolio-${portfolio.id}-prop-${prop.property_id}`}
                                    checked={inSelection}
                                    onCheckedChange={() => {
                                      if (inSelection) {
                                        removeFromDealRoom(prop.property_id);
                                      } else {
                                        addToDealRoom(prop);
                                      }
                                    }}
                                    data-testid={`checkbox-${prop.property_id}`}
                                    className="flex-shrink-0"
                                  />
                                  <div className="w-14 h-12 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                                    {prop.images?.[0] ? (
                                      <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Building2 className="w-4 h-4 text-gray-400" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-[#1A1A2E] truncate text-sm">{prop.title}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {prop.location} · {prop.bedrooms}bd/{prop.bathrooms}ba · Est. {prop.cap_rate?.toFixed(1)}% cap*
                                    </p>
                                  </div>
                                  <span className="font-semibold text-[#1A1A2E] flex-shrink-0">{formatPrice(prop.price)}</span>
                                </label>
                              );
                            })}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Closing-cost eligibility disclosure */}
        {relinquishedValue && parseFloat(relinquishedValue) > 0 && (
          <details
            id="closing-cost-details"
            className="mb-8 bg-slate-50 border border-slate-200 rounded-xl group scroll-mt-24"
            data-testid="closing-cost-disclosure"
          >
            <summary className="flex items-center gap-2 cursor-pointer px-5 py-3 text-sm font-medium text-[#1A1A2E] list-none">
              <ChevronDown className="w-4 h-4 text-gray-400 group-open:hidden" />
              <ChevronUp className="w-4 h-4 text-gray-400 hidden group-open:block" />
              What's included in the closing-cost estimate (and what isn't)
            </summary>
            <div className="px-5 pb-5 pt-2 border-t border-slate-200 text-sm text-slate-700 space-y-4">
              <div>
                <p className="font-semibold text-emerald-700 mb-2">✓ Included — 1031-eligible closing costs</p>
                <p className="text-xs text-gray-500 mb-2">
                  Per IRS Rev. Rul. 72-456 and Treas. Reg. §1.1031(k)-1(g)(7), these can be paid from exchange funds without creating taxable boot:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  <div>
                    <p className="font-medium text-[#1A1A2E] mb-1">NYC (~3.0%)</p>
                    <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                      <li>Mansion Tax (1–3.9%, tier-based)</li>
                      <li>NYS + NYC transfer-tax reimbursement (~1.825%, sponsor sales)</li>
                      <li>Owner's title insurance (~0.45%)</li>
                      <li>Attorney fees (~0.10%)</li>
                      <li>Recording fees</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A2E] mb-1">Miami (~1.5%)</p>
                    <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                      <li>Doc stamps on deed (0.60%, buyer on sponsor sales)</li>
                      <li>Owner's title insurance (~0.55%)</li>
                      <li>Attorney fees (~0.10%)</li>
                      <li>Recording + minor fees</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-semibold text-amber-700 mb-2">✗ NOT Included — must be paid outside exchange funds</p>
                <p className="text-xs text-gray-500 mb-2">
                  These costs create taxable boot if paid from 1031 proceeds; budget separately:
                </p>
                <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <li>Mortgage/loan costs (points, lender's title, appraisal for lender)</li>
                  <li>HOA transfer, move-in, or estoppel fees</li>
                  <li>Prorated HOA dues, property tax, insurance, utilities</li>
                  <li>Security deposits, prepaid rents</li>
                  <li>Repairs, capital contributions, working capital</li>
                  <li>Non-transactional due diligence (e.g. personal consulting)</li>
                </ul>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                <strong>Important:</strong> These are planning estimates based on typical sponsor-sale transactions in each market. Your Qualified Intermediary (QI) and closing attorney will provide the authoritative list of eligible expenses for your specific transaction. 1031Condo.com is a real estate brokerage — we do not provide tax or legal advice.
              </div>
            </div>
          </details>
        )}

        {/* Deal Room Section */}
        <motion.div 
          id="step3-deal-room"
          className="bg-white rounded-2xl border border-gray-200 p-6 mb-8 shadow-sm scroll-mt-24"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h2 className="text-lg font-bold text-[#1A1A2E]">Deal Room</h2>
              <p className="text-sm text-gray-500">
                {dealRoomProperties.length > 0 
                  ? `${dealRoomProperties.length} ${dealRoomProperties.length === 1 ? 'property' : 'properties'} selected — adjust offer prices below.`
                  : 'Check boxes on Suggested Combinations above to start building your selection.'
                }
              </p>
            </div>
            {dealRoomProperties.length > 0 && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDealRoom(!showDealRoom)}
                  className="text-gray-600"
                >
                  {showDealRoom ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {showDealRoom ? 'Collapse' : 'Expand'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearDealRoom}
                  className="text-red-600 hover:bg-red-50 hover:border-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </div>

          {dealRoomProperties.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Your Deal Room is empty</p>
              <p className="text-sm text-gray-500 mt-1">
                Click the <Plus className="w-4 h-4 inline" /> button next to properties in the suggestions above to add them
              </p>
            </div>
          ) : showDealRoom && (
            <div className="space-y-4">
              {/* Relinquished Property Target */}
              {relinquishedValue && parseFloat(relinquishedValue) > 0 && (() => {
                const ts = dealRoomTargetStyle(dealRoomTotals.remainingFunds);
                return (
                <div className={`p-4 rounded-xl ${ts.container}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Target from Relinquished Property</p>
                      <p className="text-2xl font-bold text-[#1A1A2E]">{formatPrice(dealRoomTotals.relinquishedAmount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-700">{ts.label}</p>
                      <p className={`text-2xl font-bold ${ts.value}`}>
                        {formatPrice(Math.abs(dealRoomTotals.remainingFunds))}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* Properties Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="deal-room-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 font-semibold text-gray-700">Property</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Asking Price</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Offer Price</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Proj. Rent</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Common Charges</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Property Tax</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Monthly Net</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Annual Net</th>
                      <th className="text-right py-3 px-2 font-semibold text-gray-700">Est. Cap Rate*</th>
                      <th className="text-center py-3 px-2 font-semibold text-gray-700">LOI</th>
                      <th className="text-center py-3 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealRoomProperties.map((prop, idx) => {
                      const monthlyExpenses = (prop.monthly_common_charges || 0) + (prop.monthly_taxes || 0);
                      const monthlyNet = (prop.projected_monthly_rent || 0) - monthlyExpenses;
                      const annualNet = monthlyNet * 12;
                      const capRate = prop.offer_price > 0 ? (annualNet / prop.offer_price) * 100 : 0;
                      const existingLoi = getLoiForProperty(prop.property_id);
                      
                      return (
                        <tr key={prop.property_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-10 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                                {prop.images?.[0] ? (
                                  <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-[#1A1A2E] truncate max-w-[150px]">{prop.title}</p>
                                <p className="text-xs text-gray-500">{prop.bedrooms}bd/{prop.bathrooms}ba</p>
                              </div>
                            </div>
                          </td>
                          <td className="text-right py-3 px-2 text-gray-600">
                            {formatPrice(prop.price)}
                          </td>
                          <td className="text-right py-3 px-2">
                            <div className="relative inline-block">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                              <Input
                                type="number"
                                value={prop.offer_price || ''}
                                onChange={(e) => updateOfferPrice(prop.property_id, e.target.value)}
                                className="w-28 h-8 text-right pr-2 pl-5 text-sm font-medium"
                                data-testid={`offer-price-${prop.property_id}`}
                              />
                            </div>
                          </td>
                          <td className="text-right py-3 px-2 text-gray-600">
                            {formatPrice(prop.projected_monthly_rent || 0)}
                          </td>
                          <td className="text-right py-3 px-2 text-gray-600">
                            {formatPrice(prop.monthly_common_charges || 0)}
                          </td>
                          <td className="text-right py-3 px-2 text-gray-600">
                            {formatPrice(prop.monthly_taxes || 0)}
                          </td>
                          <td className={`text-right py-3 px-2 font-medium ${monthlyNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPrice(monthlyNet)}
                          </td>
                          <td className={`text-right py-3 px-2 font-medium ${annualNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPrice(annualNet)}
                          </td>
                          <td className={`text-right py-3 px-2 font-semibold ${
                            capRate >= 5 ? 'text-green-600' : capRate >= 3 ? 'text-yellow-600' : 'text-gray-600'
                          }`}>
                            {capRate.toFixed(2)}%
                          </td>
                          <td className="text-center py-3 px-2">
                            {existingLoi ? (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => downloadLoiPdf(existingLoi.loi_id)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Download LOI PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openLoiModal(prop, existingLoi)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit LOI"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <span className="text-xs text-gray-500 ml-1">Rev {existingLoi.revision_number}</span>
                              </div>
                            ) : (
                              <button
                                onClick={() => openLoiModal(prop)}
                                className="p-1.5 text-[#0066FF] hover:bg-blue-50 rounded transition-colors flex items-center gap-1"
                                title="Create LOI"
                              >
                                <FileText className="w-4 h-4" />
                                <span className="text-xs">Create</span>
                              </button>
                            )}
                          </td>
                          <td className="text-center py-3 px-2">
                            <button
                              onClick={() => removeFromDealRoom(prop.property_id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              data-testid={`remove-${prop.property_id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-4 px-2 text-[#1A1A2E]">
                        TOTALS ({dealRoomTotals.propertyCount} {dealRoomTotals.propertyCount === 1 ? 'property' : 'properties'})
                      </td>
                      <td className="text-right py-4 px-2 text-[#1A1A2E]">
                        {formatPrice(dealRoomTotals.totalAskingPrice)}
                      </td>
                      <td className="text-right py-4 px-2 text-[#0066FF]">
                        {formatPrice(dealRoomTotals.totalOfferPrice)}
                      </td>
                      <td className="text-right py-4 px-2 text-[#1A1A2E]">
                        {formatPrice(dealRoomTotals.totalProjectedRent)}
                      </td>
                      <td className="text-right py-4 px-2 text-[#1A1A2E]">
                        {formatPrice(dealRoomTotals.totalCommonCharges)}
                      </td>
                      <td className="text-right py-4 px-2 text-[#1A1A2E]">
                        {formatPrice(dealRoomTotals.totalPropertyTaxes)}
                      </td>
                      <td className={`text-right py-4 px-2 ${dealRoomTotals.totalMonthlyNetProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPrice(dealRoomTotals.totalMonthlyNetProfit)}
                      </td>
                      <td className={`text-right py-4 px-2 ${dealRoomTotals.totalAnnualNetProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPrice(dealRoomTotals.totalAnnualNetProfit)}
                      </td>
                      <td className={`text-right py-4 px-2 ${
                        dealRoomTotals.avgCapRate >= 5 ? 'text-green-600' : 
                        dealRoomTotals.avgCapRate >= 3 ? 'text-yellow-600' : 'text-gray-600'
                      }`}>
                        {dealRoomTotals.avgCapRate.toFixed(2)}%
                      </td>
                      <td className="text-center py-4 px-2 text-xs text-gray-500">
                        {lois.filter(l => dealRoomProperties.some(p => p.property_id === l.property?.property_id)).length} LOIs
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* LOI Summary Section */}
              {lois.filter(l => dealRoomProperties.some(p => p.property_id === l.property?.property_id)).length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-[#1A1A2E]">Letters of Intent</h4>
                  </div>
                  <div className="space-y-2">
                    {dealRoomLois.map(loi => (
                        <div key={loi.loi_id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium text-[#1A1A2E] text-sm">{loi.property?.title}</p>
                              <p className="text-xs text-gray-500">
                                Rev {loi.revision_number} • {formatLoiDate(loi.updated_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#0066FF]">
                              {formatPrice(loi.property?.offer_price || 0)}
                            </span>
                            <button
                              onClick={() => downloadLoiPdf(loi.loi_id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const prop = dealRoomProperties.find(p => p.property_id === loi.property?.property_id);
                                if (prop) openLoiModal(prop, loi);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit LOI"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteLoi(loi.loi_id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete LOI"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Savings from Asking:</span>{' '}
                  <span className={dealRoomTotals.totalAskingPrice - dealRoomTotals.totalOfferPrice >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {formatPrice(dealRoomTotals.totalAskingPrice - dealRoomTotals.totalOfferPrice)}
                  </span>
                </div>
                <div className="flex gap-3">
                  <Link to="/contact">
                    <Button variant="outline">
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Agent
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Inline Next Steps (only visible with selected properties) */}
        {dealRoomProperties.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8 flex items-center justify-between flex-wrap gap-3" data-testid="next-steps-inline">
            <div>
              <p className="font-semibold text-[#1A1A2E]">Ready to submit?</p>
              <p className="text-sm text-gray-600">Generate LOIs from the Deal Room above, then reach out to your broker to negotiate.</p>
            </div>
            <Link to="/contact">
              <Button className="btn-primary" data-testid="inline-contact-broker-btn">
                <Mail className="w-4 h-4 mr-2" />
                Contact My Broker
              </Button>
            </Link>
          </div>
        )}

        {/* Full inventory — hidden behind toggle */}
        <details className="mb-8 group" data-testid="full-inventory-toggle">
          <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-[#0066FF] list-none hover:underline">
            <ChevronDown className="w-4 h-4 group-open:hidden" />
            <ChevronUp className="w-4 h-4 hidden group-open:block" />
            Browse all {properties.length} available properties
          </summary>
          <div className="pt-6">
        {loadingProperties ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner w-8 h-8" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property, idx) => (
              <motion.div
                key={property.property_id}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                data-testid={`portal-property-${property.property_id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={property.images?.[0] || "https://images.unsplash.com/photo-1757621448471-cf5fcd2b84a7?w=800"}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="px-2 py-1 bg-white/90 text-xs font-semibold rounded">
                      {property.market === "nyc" ? "NYC" : "Miami"}
                    </span>
                    {property.featured && (
                      <span className="px-2 py-1 bg-[#0066FF] text-white text-xs font-semibold rounded">
                        Featured
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="text-lg font-bold text-[#1A1A2E] mb-1">{property.title}</h3>
                  <div className="flex items-center gap-1 text-gray-500 text-sm mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{property.location}</span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-1">
                      <Bed className="w-4 h-4" />
                      <span>{property.bedrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath className="w-4 h-4" />
                      <span>{property.bathrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Square className="w-4 h-4" />
                      <span>{property.sqft?.toLocaleString()} SF</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-xl font-bold text-[#0066FF]">
                      {formatPrice(property.price)}
                    </span>
                    <Link to={`/properties/${property.property_id}`}>
                      <Button variant="outline" size="sm" className="text-[#0066FF] border-[#0066FF]/30 hover:bg-blue-50">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
          </div>
        </details>
        </>
        )}

        {/* Bottom Consultation CTA — visible on every portal tab */}
        <motion.div
          className="mt-12 mb-4 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-8 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="bottom-consultation-cta"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="text-xl font-bold text-[#1A1A2E] mb-2">
                Ready to discuss your 1031 exchange?
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                A licensed broker will follow up to walk through your timeline, replacement criteria,
                and the specific units that fit your exchange. No commitment required.
              </p>
            </div>
            <Link
              to="/contact"
              state={{
                consultationRequest: true,
                prefillName: user.name,
                prefillEmail: user.email,
              }}
              className="shrink-0"
            >
              <Button
                size="lg"
                className="btn-primary px-8 py-6 text-base font-semibold shadow-md"
                data-testid="bottom-request-consultation-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Request a Consultation
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>

      {/* LOI Modal */}
      <AnimatePresence>
        {showLoiModal && loiProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeLoiModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[#1A1A2E]">
                      {editingLoi ? 'Edit Letter of Intent' : 'Create Letter of Intent'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">{loiProperty.title}</p>
                  </div>
                  <button
                    onClick={closeLoiModal}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Non-Binding Disclaimer */}
              <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Non-Binding Document</p>
                    <p className="text-amber-700 text-xs mt-1">
                      This Letter of Intent is non-binding and is submitted solely for negotiating purposes. 
                      It does not create any legal obligation. A binding Agreement of Sale will be drafted 
                      by an attorney if the parties agree to proceed.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleLoiSubmit} className="p-6 space-y-6">
                {/* Property Summary */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-[#1A1A2E] mb-3">Property Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Asking Price:</span>
                      <span className="ml-2 font-semibold">{formatPrice(loiProperty.price)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Your Offer:</span>
                      <span className="ml-2 font-semibold text-[#0066FF]">{formatPrice(loiProperty.offer_price || loiProperty.price)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Market:</span>
                      <span className="ml-2">{loiProperty.market === 'nyc' ? 'New York City' : 'Miami'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Size:</span>
                      <span className="ml-2">{loiProperty.sqft?.toLocaleString() || 'N/A'} SF</span>
                    </div>
                  </div>
                </div>

                {/* Buyer Information */}
                <div>
                  <h3 className="font-semibold text-[#1A1A2E] mb-3">Buyer Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <Input
                        value={loiForm.buyer_name}
                        onChange={(e) => setLoiForm({...loiForm, buyer_name: e.target.value})}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name (if applicable)</label>
                      <Input
                        value={loiForm.buyer_entity_name}
                        onChange={(e) => setLoiForm({...loiForm, buyer_entity_name: e.target.value})}
                        placeholder="ABC Investments LLC"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <Input
                        type="email"
                        value={loiForm.buyer_email}
                        onChange={(e) => setLoiForm({...loiForm, buyer_email: e.target.value})}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <Input
                        value={loiForm.buyer_phone}
                        onChange={(e) => setLoiForm({...loiForm, buyer_phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <Input
                        value={loiForm.buyer_address}
                        onChange={(e) => setLoiForm({...loiForm, buyer_address: e.target.value})}
                        placeholder="123 Main St, City, State ZIP"
                      />
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div>
                  <h3 className="font-semibold text-[#1A1A2E] mb-3">Offer Terms</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Earnest Money Deposit (%)</label>
                      <Input
                        type="number"
                        value={loiForm.earnest_money_percent}
                        onChange={(e) => setLoiForm({...loiForm, earnest_money_percent: e.target.value})}
                        min="1"
                        max="30"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        = {formatPrice((loiProperty.offer_price || loiProperty.price) * (parseFloat(loiForm.earnest_money_percent) / 100))}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Period (days)</label>
                      <Input
                        type="number"
                        value={loiForm.inspection_period_days}
                        onChange={(e) => setLoiForm({...loiForm, inspection_period_days: e.target.value})}
                        min="7"
                        max="30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Financing Contingency (days)</label>
                      <Input
                        type="number"
                        value={loiForm.financing_contingency_days}
                        onChange={(e) => setLoiForm({...loiForm, financing_contingency_days: e.target.value})}
                        min="14"
                        max="60"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Closing (days from acceptance)</label>
                      <Input
                        type="number"
                        value={loiForm.closing_days_from_acceptance}
                        onChange={(e) => setLoiForm({...loiForm, closing_days_from_acceptance: e.target.value})}
                        min="30"
                        max="180"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Closing Date (optional)</label>
                      <Input
                        type="date"
                        value={loiForm.closing_date}
                        onChange={(e) => setLoiForm({...loiForm, closing_date: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Terms */}
                <div>
                  <h3 className="font-semibold text-[#1A1A2E] mb-3">Additional Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Additional Terms</label>
                      <Textarea
                        value={loiForm.additional_terms}
                        onChange={(e) => setLoiForm({...loiForm, additional_terms: e.target.value})}
                        placeholder="Any additional terms or conditions..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Exclusions</label>
                      <Textarea
                        value={loiForm.exclusions}
                        onChange={(e) => setLoiForm({...loiForm, exclusions: e.target.value})}
                        placeholder="Any items to be excluded from the sale..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* Revision Info (if editing) */}
                {editingLoi && (
                  <div className="bg-blue-50 rounded-lg p-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      Currently Rev {editingLoi.revision_number} • Last updated {formatLoiDate(editingLoi.updated_at)}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeLoiModal}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 btn-primary"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingLoi ? `Save as Rev ${editingLoi.revision_number + 1}` : 'Create LOI'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
